import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, Search, RefreshCw, Trash2, FileText, Copy, ExternalLink, Briefcase, Landmark } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  NOVO: { label: "Novo", cor: "bg-blue-100 text-blue-700" },
  EM_ANALISE: { label: "Em análise", cor: "bg-amber-100 text-amber-700" },
  APROVADO: { label: "Aprovado", cor: "bg-emerald-100 text-emerald-700" },
  REPROVADO: { label: "Reprovado", cor: "bg-red-100 text-red-600" },
  CONCLUIDO: { label: "Concluído", cor: "bg-violet-100 text-violet-700" },
};
const statusLabel = (s) => STATUS_META[s]?.label || s || "—";
const statusCor = (s) => STATUS_META[s]?.cor || "bg-slate-100 text-slate-600";

const SEG_META = {
  "CLT": { icon: Briefcase, cor: "bg-cyan-100 text-cyan-700" },
  "Servidor Federal": { icon: Landmark, cor: "bg-blue-100 text-blue-700" },
};

const fmtDT = (d) => (d ? new Date(d).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" }) : "—");
const brl = (v) => (v ? Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "—");

export default function LandingLeads() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, por_segmento: {}, com_documentos: 0 });
  const [segmento, setSegmento] = useState("TODOS");
  const [statusFiltro, setStatusFiltro] = useState("TODOS");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (segmento !== "TODOS") params.append("segmento", segmento);
      if (statusFiltro !== "TODOS") params.append("status", statusFiltro);
      if (search.trim()) params.append("search", search.trim());
      const r = await axios.get(`${API}/landing-leads?${params.toString()}`);
      setLeads(r.data.items || []);
      setStats(r.data.stats || {});
    } catch {
      toast.error("Erro ao carregar leads");
    } finally {
      setLoading(false);
    }
  }, [segmento, statusFiltro, search]);

  useEffect(() => { carregar(); }, [carregar]);

  const mudarStatus = async (lead, novo) => {
    try {
      await axios.patch(`${API}/landing-leads/${lead.fonte}/${lead.id}`, { status: novo });
      toast.success("Status atualizado");
      carregar();
    } catch { toast.error("Erro ao atualizar status"); }
  };

  const excluir = async (lead) => {
    if (!window.confirm("Excluir este lead e seus documentos?")) return;
    try {
      await axios.delete(`${API}/landing-leads/${lead.fonte}/${lead.id}`);
      toast.success("Lead excluído");
      carregar();
    } catch { toast.error("Erro ao excluir"); }
  };

  const copiar = (path, nome) => {
    navigator.clipboard.writeText(`${BACKEND}${path}`);
    toast.success(`Link ${nome} copiado!`);
  };

  const docLink = (arq) => (arq?.url ? `${BACKEND}${arq.url}` : null);

  return (
    <div className="p-6 space-y-6" data-testid="landing-leads-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Mail className="h-6 w-6 text-blue-700" /> E-Mail / Landing Page
          </h1>
          <p className="text-sm text-muted-foreground">Leads recebidos pelas landings de e-mail marketing — todos os segmentos unificados</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => copiar("/simular", "CLT")} className="gap-1.5" data-testid="ll-copy-clt">
            <Copy className="h-3.5 w-3.5" /> Link CLT
          </Button>
          <Button variant="outline" size="sm" onClick={() => copiar("/simular_federal", "Servidor Federal")} className="gap-1.5" data-testid="ll-copy-federal">
            <Copy className="h-3.5 w-3.5" /> Link Servidor Federal
          </Button>
          <Button variant="outline" size="sm" onClick={carregar} className="gap-1.5" data-testid="ll-refresh">
            <RefreshCw className="h-3.5 w-3.5" /> Atualizar
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card data-testid="ll-stat-total"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-blue-700">{stats.total || 0}</p>
          <p className="text-[11px] text-gray-500 uppercase">Total de leads</p>
        </CardContent></Card>
        <Card data-testid="ll-stat-clt"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-cyan-600">{stats.por_segmento?.["CLT"] || 0}</p>
          <p className="text-[11px] text-gray-500 uppercase">CLT</p>
        </CardContent></Card>
        <Card data-testid="ll-stat-federal"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-blue-600">{stats.por_segmento?.["Servidor Federal"] || 0}</p>
          <p className="text-[11px] text-gray-500 uppercase">Servidor Federal</p>
        </CardContent></Card>
        <Card data-testid="ll-stat-docs"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-emerald-600">{stats.com_documentos || 0}</p>
          <p className="text-[11px] text-gray-500 uppercase">Com documentos</p>
        </CardContent></Card>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <Input className="pl-9" placeholder="Buscar por nome, CPF, telefone ou e-mail..." value={search}
            onChange={(e) => setSearch(e.target.value)} data-testid="ll-search" />
        </div>
        <Select value={segmento} onValueChange={setSegmento}>
          <SelectTrigger className="w-[200px]" data-testid="ll-segmento-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="TODOS">Todos os segmentos</SelectItem>
            <SelectItem value="CLT">CLT</SelectItem>
            <SelectItem value="SERVIDOR_FEDERAL">Servidor Federal</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFiltro} onValueChange={setStatusFiltro}>
          <SelectTrigger className="w-[180px]" data-testid="ll-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="TODOS">Todos os status</SelectItem>
            {Object.keys(STATUS_META).map((st) => <SelectItem key={st} value={st}>{STATUS_META[st].label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-10 text-gray-400">Carregando...</div>
          ) : leads.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Mail className="h-10 w-10 mx-auto mb-2 opacity-20" />
              <p className="font-medium">Nenhum lead ainda</p>
              <p className="text-sm">Os leads enviados pelas landings aparecerão aqui.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Segmento</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>CPF</TableHead>
                  <TableHead>Telefone</TableHead>
                  <TableHead>E-mail</TableHead>
                  <TableHead>Progresso / Documentos</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Recebido em</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((l) => {
                  const seg = SEG_META[l.segmento] || {};
                  const SegIcon = seg.icon || Mail;
                  const cc = l.arquivos?.contracheque;
                  const ex = l.arquivos?.extrato;
                  return (
                    <TableRow key={`${l.fonte}-${l.id}`} data-testid={`ll-row-${l.id}`}>
                      <TableCell>
                        <Badge className={`${seg.cor || "bg-slate-100 text-slate-600"} text-[10px] gap-1`}>
                          <SegIcon className="h-3 w-3" /> {l.segmento}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">{l.nome || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{l.cpf || "—"}</TableCell>
                      <TableCell className="text-sm">{l.telefone || "—"}</TableCell>
                      <TableCell className="text-xs">{l.email || "—"}</TableCell>
                      <TableCell>
                        {l.fonte === "servidor_federal" ? (
                          <div className="flex flex-col gap-1">
                            {cc ? (
                              <a href={docLink(cc)} target="_blank" rel="noopener noreferrer" data-testid={`ll-doc-cc-${l.id}`}
                                className="flex items-center gap-1 text-xs text-blue-700 hover:underline">
                                <FileText className="h-3.5 w-3.5" /> Contracheque <ExternalLink className="h-3 w-3" />
                              </a>
                            ) : <span className="text-xs text-gray-400">sem contracheque</span>}
                            {ex ? (
                              <a href={docLink(ex)} target="_blank" rel="noopener noreferrer" data-testid={`ll-doc-ex-${l.id}`}
                                className="flex items-center gap-1 text-xs text-blue-700 hover:underline">
                                <FileText className="h-3.5 w-3.5" /> Extrato <ExternalLink className="h-3 w-3" />
                              </a>
                            ) : <span className="text-xs text-gray-400">sem extrato</span>}
                            {l.observacao && <span className="text-[11px] text-slate-500 italic max-w-[200px] truncate" title={l.observacao}>"{l.observacao}"</span>}
                          </div>
                        ) : (
                          <div className="flex flex-col gap-1">
                            {l.etapa && <Badge className="bg-slate-100 text-slate-600 text-[10px] w-fit">➜ {l.etapa}</Badge>}
                            {l.valor_disponivel ? <span className="text-xs font-semibold text-emerald-600">Disponível: {brl(l.valor_disponivel)}</span> : null}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {l.fonte === "servidor_federal" ? (
                          <Select value={l.status} onValueChange={(v) => mudarStatus(l, v)}>
                            <SelectTrigger className={`h-7 w-[140px] text-xs ${statusCor(l.status)} border-0`} data-testid={`ll-status-${l.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.keys(STATUS_META).map((st) => <SelectItem key={st} value={st}>{STATUS_META[st].label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge className={`${statusCor(l.status)} text-[10px]`}>{statusLabel(l.status)}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">{fmtDT(l.data)}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => excluir(l)} data-testid={`ll-del-${l.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      {stats.total > 500 && <p className="text-xs text-gray-500">{stats.total} leads (mostrando os 500 mais recentes)</p>}
    </div>
  );
}
